import React, { useRef, useEffect, useState } from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';

// Video Card Component - Safari iOS optimized
const VideoCard = ({ video, poster, name, className, imagePosition }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isVideoReady, setIsVideoReady] = useState(false);

  const positionStyle = imagePosition ? {
    objectPosition: imagePosition
  } : {};

  useEffect(() => {
    const videoElement = videoRef.current;
    const container = containerRef.current;
    if (!videoElement || !container) return;

    // Force muted attribute for Safari
    videoElement.muted = true;
    videoElement.setAttribute('muted', '');
    videoElement.setAttribute('playsinline', '');
    videoElement.setAttribute('webkit-playsinline', '');

    const playVideo = () => {
      const playPromise = videoElement.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          setIsVideoReady(true);
        }).catch(() => {
          // Retry after a short delay
          setTimeout(() => {
            videoElement.play().then(() => setIsVideoReady(true)).catch(() => {});
          }, 100);
        });
      }
    };

    const handleCanPlayThrough = () => {
      playVideo();
    };

    const handleEnded = () => {
      videoElement.pause();
    };

    videoElement.addEventListener('canplaythrough', handleCanPlayThrough);
    videoElement.addEventListener('ended', handleEnded);

    // IntersectionObserver to play when visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            playVideo();
          }
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(container);

    // Try to play immediately
    if (videoElement.readyState >= 3) {
      playVideo();
    }

    return () => {
      observer.disconnect();
      videoElement.removeEventListener('canplaythrough', handleCanPlayThrough);
      videoElement.removeEventListener('ended', handleEnded);
    };
  }, []);

  return (
    <div ref={containerRef} className="relative w-full h-full">
      <video
        ref={videoRef}
        className={className}
        style={positionStyle}
        preload="auto"
        muted
        playsInline
        disablePictureInPicture
        disableRemotePlayback
      >
        <source src={video} type="video/mp4" />
      </video>
      {/* Poster image overlay */}
      <img
        src={poster}
        alt={`${name} - Haartransplantation Spezialist Dion Hair Clinic Mönchengladbach`}
        loading="lazy"
        style={positionStyle}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
          isVideoReady ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      />
    </div>
  );
};

const Team = () => {
  const { team } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  
  // Team member translations
  const teamTranslations = {
    roles: {
      'Facharzt für Thoraxchirurgie & Haartransplantationschirurg': {
        en: 'Specialist in Thoracic Surgery & Hair Transplant Surgeon'
      },
      'Fachärztin für Innere Medizin und Expertin für Haartransplantationen': {
        en: 'Specialist in Internal Medicine and Hair Transplant Expert'
      },
      'Medizinische Haartechnikerin': {
        en: 'Medical Hair Technician'
      },
      'Spezialistin für Haartransplantationen': {
        en: 'Hair Transplant Specialist'
      },
    },
    descriptions: {
      'Dr. Tsomkopoulos ist Facharzt für Thoraxchirurgie mit Approbation in Deutschland und der Schweiz. Er verfügt über langjährige Erfahrung als leitender Haartransplantationschirurg in renommierten Kliniken in Berlin, Basel und Thessaloniki.': {
        en: 'Dr. Tsomkopoulos is a specialist in thoracic surgery licensed in Germany and Switzerland. He has many years of experience as a leading hair transplant surgeon in renowned clinics in Berlin, Basel and Thessaloniki.'
      },
      'Mit ihrer umfassenden medizinischen Expertise und spezialisiertem Wissen im Bereich Haartransplantationen sorgt Dr. Bevapi für höchste medizinische Standards und optimale Behandlungsergebnisse.': {
        en: 'With her comprehensive medical expertise and specialized knowledge in hair transplantation, Dr. Bevapi ensures the highest medical standards and optimal treatment results.'
      },
      'Dilek Ergüney bringt umfangreiche Erfahrung in der präzisen Durchführung von Haartransplantationstechniken mit und ist spezialisiert auf die sorgfältige Handhabung von Haarfollikeln für optimale Ergebnisse.': {
        en: 'Dilek Ergüney brings extensive experience in precisely performing hair transplant techniques and specializes in the careful handling of hair follicles for optimal results.'
      },
      'Songül Bulut absolvierte ihr Medizinstudium in der Türkei und sammelte jahrelange Erfahrung in führenden Haarkliniken weltweit. Ihre internationale Expertise garantiert erstklassige Behandlungsergebnisse.': {
        en: 'Songül Bulut completed her medical studies in Turkey and gained years of experience in leading hair clinics worldwide. Her international expertise guarantees first-class treatment results.'
      },
    },
    specializations: {
      'ISHRS-Mitglied, FUE & DHI Spezialist, Approbation DE/CH': {
        en: 'ISHRS Member, FUE & DHI Specialist, Licensed in DE/CH'
      },
      'Fachärztin für Innere Medizin, Spezialisierung auf Haartransplantationstechniken': {
        en: 'Specialist in Internal Medicine, Specialization in Hair Transplant Techniques'
      },
      'Zertifizierte medizinische Haartechnikerin mit Spezialisierung auf FUE- und DHI-Techniken': {
        en: 'Certified Medical Hair Technician with Specialization in FUE and DHI Techniques'
      },
      'Medizinstudium in der Türkei, internationale Erfahrung in führenden Haarkliniken': {
        en: 'Medical Studies in Turkey, International Experience in Leading Hair Clinics'
      },
    },
  };

  const getTranslatedRole = (role) => {
    if (language === 'de') return role;
    return teamTranslations.roles[role]?.en || role;
  };

  const getTranslatedDescription = (desc) => {
    if (language === 'de') return desc;
    return teamTranslations.descriptions[desc]?.en || desc;
  };

  const getTranslatedSpecialization = (spec) => {
    if (language === 'de') return spec;
    return teamTranslations.specializations[spec]?.en || spec;
  };
  
  return (
    <section id="team" className={`py-24 relative overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-[#0a0a0a]' : 'bg-white'
    }`}>
      {/* Background */}
      <div className="absolute inset-0">
        <div className={`absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full blur-[80px] ${
          isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#5A8BA6]/5'
        }`} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${
            isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'
          }`}>
            {t('team.sectionBadge')}
          </span>
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {language === 'de' ? 'Unser ' : 'Our '}
            <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">Team</span>
          </h2>
          <p className={`max-w-2xl mx-auto text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('team.sectionDescription')}
          </p>
        </div>

        {/* Team Grid - 4 columns for 4 team members */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {team.map((member) => {
            // Special positioning for Dr. Tsomkopoulos (id 1) - positioned lower to show more body
            const isFirstMember = member.id === 1;
            const imagePosition = isFirstMember ? 'center 15%' : 'center 20%';
            
            // Get translated team member data
            const translatedRole = getTranslatedRole(member.role);
            const translatedDesc = member.description ? getTranslatedDescription(member.description) : '';
            const translatedSpec = getTranslatedSpecialization(member.specialization);
            
            return (
              <div
                key={member.id}
                className="group relative h-full"
              >
                {/* Card */}
                <div className={`relative rounded-2xl overflow-hidden border transition-all duration-500 h-full flex flex-col card-lift ${
                  isDark 
                    ? 'bg-[#1a1a1a] border-white/5 hover:border-[#7BA7C2]/30'
                    : 'bg-white border-gray-200 hover:border-[#5A8BA6]/30'
                }`}>
                  {/* Image or Video - Fixed height */}
                  <div className="relative h-80 overflow-hidden flex-shrink-0">
                    {member.video ? (
                      <VideoCard
                        video={member.video}
                        poster={member.image}
                        name={member.name}
                        imagePosition={imagePosition}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <img
                        src={member.image}
                        alt={`${member.name} - ${member.role || 'Team'} Dion Hair Clinic Mönchengladbach`}
                        loading="lazy"
                        style={{ objectPosition: imagePosition }}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    )}
                    <div className={`absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t to-transparent ${
                      isDark ? 'from-[#1a1a1a]' : 'from-white'
                    }`} />
                  </div>

                  {/* Info - Flex grow to fill space */}
                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className={`text-xl font-semibold mb-2 transition-colors duration-300 ${
                      isDark 
                        ? 'text-white group-hover:text-[#7BA7C2]'
                        : 'text-gray-900 group-hover:text-[#5A8BA6]'
                    }`}>
                      {member.name}
                    </h3>
                    <p className={`text-sm font-medium mb-3 h-10 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>{translatedRole}</p>
                    {translatedDesc && (
                      <p className={`text-sm mb-4 leading-relaxed flex-grow ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{translatedDesc}</p>
                    )}
                    <p className={`text-xs mt-auto ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{translatedSpec}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Team;